import React, { useState } from 'react';
import { Badge, Button, Input } from 'reactstrap';
import cs_analysis_fetch from '../../../utils/cs_analysis_fetch';
import TaskCreateModal from './TaskCreateModal';
import { TASK_STATUS_COLOR } from './task_ui';
import { describe_linked_actionable, filter_linkable_tasks, task_prefill_from_ticket } from './task_ticket_links';

/**
 * "Tasks" card in the client-ticket drawer (refinement A, 2026-09-12): the
 * tasks linked to this ticket (title · status · owner · due), "Create task"
 * (TaskCreateModal prefilled from the ticket; saves through
 * from-client-ticket/:ticketId) and "Link existing task" (picker over the
 * client's open tasks → link-client-ticket). Mirrors the Product tickets card
 * above it. Shared by the org tab and the cross-client queue via TicketDrawer.
 *
 * store_id is the client's store id (the actionables API is store-keyed);
 * without one (bare ticket detail with no client row) the card is read-only
 * and says why.
 */
const TicketTasksCard = ({ ticket, store_id, linked, reps_info, busy, on_changed, styles }) => {
    const [show_create, set_show_create] = useState(false);
    const [picking, set_picking] = useState(false);
    const [q, set_q] = useState('');
    const [tasks, set_tasks] = useState(null); // client's tasks, loaded when the picker opens
    const [loading, set_loading] = useState(false);
    const [err, set_err] = useState(null);

    const S = styles || {};
    const list = Array.isArray(linked) ? linked : [];
    const can_act = !!store_id && !busy;
    const rep_list = (reps_info && reps_info.reps) || [];
    const rep_role = (reps_info && reps_info.role) || 'rep';
    const self_email = (reps_info && reps_info.self && reps_info.self.email) || '';

    const open_picker = async () => {
        if (picking) { set_picking(false); set_q(''); return; }
        set_picking(true);
        if (tasks !== null) return;
        set_loading(true);
        set_err(null);
        try {
            const r = await cs_analysis_fetch({ path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables`, query: {} });
            set_tasks(Array.isArray(r) ? r : []);
        } catch (e) {
            set_tasks([]);
            set_err((e && e.message) || String(e));
        } finally {
            set_loading(false);
        }
    };

    const run = async (fn) => {
        set_err(null);
        try {
            await fn();
            set_tasks(null); // stale once links change; reloads on next open
            set_picking(false); set_q('');
            if (on_changed) await on_changed();
        } catch (e) {
            set_err((e && e.message) || String(e));
        }
    };
    const link_task = (task) => run(() => cs_analysis_fetch({
        path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${task.id}/link-client-ticket`,
        method: 'POST',
        body: { ticketId: String(ticket.id) },
    }));
    const unlink_task = (task) => run(() => cs_analysis_fetch({
        path: `/api/v1/clients/${encodeURIComponent(store_id)}/actionables/${task.id}/unlink-client-ticket`,
        method: 'POST',
        body: { ticketId: String(ticket.id) },
    }));

    const matches = picking ? filter_linkable_tasks(tasks || [], q, list.map((a) => a.id)).slice(0, 25) : [];

    return (
        <div style={S.card}>
            <div style={S.section}>
                Tasks
                {store_id ? (
                    <span style={{ float: 'right', display: 'inline-flex', gap: 6 }}>
                        <Button size='sm' color='link' disabled={!can_act} title='Create a task for this ticket (linked; completes when the ticket is done)'
                            style={{ padding: 0, fontSize: 12, textTransform: 'none' }} onClick={() => set_show_create(true)}>+ Create task</Button>
                        <Button size='sm' color='link' disabled={!can_act} title="Link one of this client's open tasks"
                            style={{ padding: 0, fontSize: 12, textTransform: 'none' }} onClick={open_picker}>{picking ? '× cancel' : '+ Link existing task'}</Button>
                    </span>
                ) : null}
            </div>
            {list.length === 0 ? (
                <div style={{ fontSize: 12, color: '#a4b0bb', marginBottom: 4 }}>
                    {store_id
                        ? 'No task yet — create one so the work this ticket needs shows up in the rep\'s Tasks.'
                        : 'Open this ticket from the client\'s page to create or link tasks.'}
                </div>
            ) : list.map((a) => (
                <div key={a.id} style={{ fontSize: 12.5, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Badge style={{ fontSize: 10, backgroundColor: TASK_STATUS_COLOR[a.status] || '#95a5a6', color: '#fff' }}>{(a.status || '').replace('_', ' ')}</Badge>
                    {store_id ? (
                        <a href={`/home/store_dashboard/${encodeURIComponent(store_id)}/actionables`} target='_blank' rel='noreferrer noopener'
                            title="Open the client's Tasks tab"
                            style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{describe_linked_actionable(a)} ↗</a>
                    ) : (
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{describe_linked_actionable(a)}</span>
                    )}
                    {store_id ? (
                        <Button size='sm' color='link' title='unlink' disabled={!can_act} style={{ padding: 0, fontSize: 12, color: '#c0392b' }}
                            onClick={() => { if (window.confirm('Unlink this task from the ticket?')) unlink_task(a); }}>✕</Button>
                    ) : null}
                </div>
            ))}
            {picking ? (
                <div style={{ marginTop: 6 }}>
                    <Input bsSize='sm' autoFocus placeholder="Filter the client's open tasks…" value={q}
                        onChange={(e) => set_q(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') { set_picking(false); set_q(''); } }}
                        style={{ fontSize: 12.5, height: 30, borderRadius: 6, border: '1px solid #d8dfe6' }} />
                    <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 4 }}>
                        {loading ? <div style={{ fontSize: 12, color: '#a4b0bb', padding: '3px 0' }}>Loading…</div>
                            : matches.length === 0 ? <div style={{ fontSize: 12, color: '#a4b0bb', padding: '3px 0' }}>No open tasks to link{q.trim() ? ' for that filter' : ''}.</div>
                                : matches.map((t) => (
                                    <div key={t.id} style={{ fontSize: 12, padding: '3px 0', display: 'flex', alignItems: 'center', gap: 6 }}>
                                        <Button size='sm' color='link' style={{ padding: 0, fontSize: 12 }} disabled={!can_act} onClick={() => link_task(t)}>+ link</Button>
                                        <span style={{ color: '#8a97a5' }}>[{(t.status || '').replace('_', ' ')}]</span>
                                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.description}>{t.title || t.description}</span>
                                    </div>
                                ))}
                    </div>
                </div>
            ) : null}
            {err ? <div style={{ fontSize: 11.5, color: '#c0392b', marginTop: 4 }}>{err}</div> : null}
            {store_id ? (
                <TaskCreateModal
                    open={show_create}
                    store_id={store_id}
                    reps={rep_list}
                    rep_role={rep_role}
                    self_email={self_email}
                    from_client_ticket={ticket}
                    prefill={task_prefill_from_ticket(ticket)}
                    on_close={() => set_show_create(false)}
                    on_created={() => { set_show_create(false); set_tasks(null); if (on_changed) on_changed(); }}
                />
            ) : null}
        </div>
    );
};

export default TicketTasksCard;
